import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDataProvider, useNotify, useRefresh, useGetList, useRecordContext } from 'react-admin';
import { Loader } from '../../Loader';
import { useForm, Controller } from 'react-hook-form';
import { DialogueContext } from '../../../components/ActionsMenu';
import { useContext } from 'react';

const UpdateGameDialog = ({ openDialog, handleDialogClose }) => {
  const { handleMenuClose } = useContext(DialogueContext);
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const [loading, setLoading] = useState(false);

  const { data: buildingData, isPending: isBuildingPending } = useGetList(
    'VegasBuilding',
    { pagination: { page: 1, perPage: 10 }, sort: { field: 'createdAt', order: 'ASC' } },
    { refetchOnWindowFocus: false, refetchOnReconnect: false }
  );

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset
  } = useForm();

  useEffect(() => {
    if (record && openDialog) {
      reset({
        name: record.name || '',
        category: record.category || '',
        description: record.description || '',
        gameSequence: record.gameSequence?.toString() || '',
        building: record.building?.id || record.building || '',
        live_version: record.live_version || '',
        slug: record.slug || '',
        gameURL: record.gameURL || '',
        objectId: record.objectId || record.id || '',
        gameId: record.gameId || ''
      });
    }
  }, [record, openDialog, reset]);

  const onSubmit = async (formData) => {
    const liveVersion = formData.live_version?.toLowerCase();
    // If user edited gameURL directly, use that; otherwise version-replace the existing one.
    let gameURL = formData.gameURL?.trim() || record?.gameURL;
    if (liveVersion && gameURL) {
      // Replace the version segment (e.g., /v8/) in the CloudFront URL
      // URL pattern: {cloudfront}/{GameName}/{version}/{DevFolder}/index.html
      gameURL = gameURL.replace(/\/v\d+\//, `/${liveVersion}/`);
    }
    const payload = {
      name: formData.name,
      category: formData.category,
      description: formData.description,
      gameSequence: parseInt(formData.gameSequence, 10),
      building: formData.building,
      live_version: liveVersion,
      slug: formData.slug?.trim() || null,
      gameURL
    };
    setLoading(true);
    try {
      const response = await dataProvider.update('GameCatalogue', {
        id: record?.id,
        data: payload
      });
      if (response) {
        notify('Game updated successfully!', { type: 'success' });
        handleClose();
        refresh();
      }
    } catch (error) {
      notify(error.message, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    handleDialogClose();
    handleMenuClose();
  };

  return (
    <Dialog
      open={openDialog}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      onKeyDown={(evt) => evt.stopPropagation()}
      PaperProps={{ sx: { backgroundColor: '#1a1a2e', color: '#fff' } }}
    >
      {loading && <Loader />}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(255, 239, 153, 0.20)'
        }}
      >
        <span style={{ color: '#FFC107' }}>Update Game</span>
        <IconButton onClick={handleClose} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="name"
                control={control}
                rules={{ required: 'Name is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Name"
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="gameSequence"
                control={control}
                rules={{ required: 'Game Sequence is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Game Sequence"
                    fullWidth
                    error={!!errors.gameSequence}
                    helperText={errors.gameSequence?.message}
                    onKeyDown={(e) => {
                      const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
                      if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="building"
                control={control}
                rules={{ required: 'Building is required' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.building}>
                    <InputLabel>Game Building</InputLabel>
                    <Select {...field} label="Game Building">
                      {isBuildingPending ? (
                        <MenuItem value="">Loading...</MenuItem>
                      ) : (
                        buildingData?.map((b) => (
                          <MenuItem key={b.id} value={b.id}>
                            {b.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="category"
                control={control}
                rules={{ required: 'Category is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Category"
                    fullWidth
                    error={!!errors.category}
                    helperText={errors.category?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="live_version"
                control={control}
                render={({ field }) => <TextField {...field} label="Live Version" fullWidth />}
              />
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="slug"
                control={control}
                render={({ field }) => <TextField {...field} label="Slug" fullWidth />}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <TextField
                label="Object ID"
                value={record?.id || ''}
                fullWidth
                InputProps={{ readOnly: true }}
                helperText="Leave empty to auto-generate. Set to keep the same ID across Dev/QA/Prod."
                FormHelperTextProps={{ sx: { color: '#FFF' } }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <TextField
                label="Game ID"
                value={record?.gameId || ''}
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="gameURL"
                control={control}
                render={({ field }) => <TextField {...field} label="Game URL" fullWidth />}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Description" fullWidth multiline rows={3} />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255, 239, 153, 0.20)' }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              border: '1px solid rgba(255, 239, 153, 0.20)',
              color: '#FFF',
              '&:hover': { border: '1px solid #fff', background: 'none' }
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              backgroundColor: '#FFD700',
              color: '#000',
              fontWeight: 500,
              '&:hover': { backgroundColor: '#FFA000' }
            }}
          >
            Update
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UpdateGameDialog;
